// **************************************************
// IMAGE SERVICE
// **************************************************
//
// Very similar to AssetService, uses same manifest
// and group structure, but uses Image.onload to preload
// images for later use.
//
// The idea is to use AssetService to load a group
// (including images) into cache, then load that same
// group with ImageService immediately after to make
// them ready to use.
//
// // load
// await AssetService.load(assetManifest, 'some_group_name', 2)
// await ImageService.load(assetManifest, 'some_group_name', 2)
//
// // get an <image>
// ImageService.get('bg')
//
// **************************************************
import Queue from 'src/helpers/Queue'
import EventEmitter from 'src/helpers/EventEmitter'
import AssetService from 'src/services/AssetService'
import { getManifest } from '@/config/manifests/images'
import Env from '@/config/env'
import console from 'src/services/ConsoleService'

const Events = {
  COMPLETED: 'completed',
  ITEM_LOADED: 'ITEM_LOADED',
}

class ImageService {
  constructor() {
    Object.assign(this, EventEmitter, {
      groups: {},
      images: {},
      _images: {},
      _loadedUrls: new Set(),
      _webp: false,
      _manifestComplied: false,
      complete: false,
    })
  }

  getManifest = async () => {
    this._webp = await Env.check_webp_feature('lossless')
    const manifest = getManifest()
    this._manifestComplied = true
    return manifest
  }

  notifyLoaded = (url, callback) => {
    var _url = url
    var _callback = callback

    if (this._loadedUrls.has(_url)) {
      _callback(this.images[_url])
    } else {
      this.on(Events.ITEM_LOADED, url_loaded => {
        if (url_loaded === _url) {
          _callback(this.images[_url])
        }
      })
    }
  }

  onUrlLoaded(url) {
    if (!this._loadedUrls.has(url)) {
      this._loadedUrls.add(url)
      this.emit(Events.ITEM_LOADED, url)
    }
  }

  _loadItem = url => {
    return new Promise(resolve => {
      let image = new window.Image()
      image.onload = () => {
        this.images[url] = image
        this._images[url] = image
        //console.log(' ------------------------ >>> ', url)
        this.onUrlLoaded(url)
        resolve()
      }
      image.crossOrigin = 'anonymous'

      // GET FROM ASSET SERVICE
      let asset = AssetService.getAsset(url)
      if (asset && asset.blobUrl) {
        // image.src = URL.createObjectURL(asset.blob)
        image.src = asset.blobUrl
      } else {
        image.src = url
      }
    })
  }

  _loadGroup = (concurrency, group) => {
    return new Promise(resolve => {
      let q = new Queue(concurrency)
      this.groups[group.id] = {}
      let item = this.groups[group.id]

      for (const url of group.urls) {
        if (!/\.(gif|jpe?g|png|webp)$/i.test(url)) continue
        q.add(() => {
          return this._loadItem(url)
        })
      }

      q.on(Queue.Events.EMPTY, () => {
        item.loaded = true
        // console.log(' ------------------------ >>> ', group.id)
        this.emit(Events.GROUP_COMPLETED, group.id)
        resolve()
      })
    })
  }

  load = (manifest, concurrency = 1, groupId = '') => {
    return new Promise(resolve => {
      let q = new Queue(concurrency)

      for (const group of manifest) {
        if (group.id !== groupId && groupId !== '') continue
        // console.log(`IMAGE LOAD GROUP ${group.id}`)

        q.add(() => {
          return this._loadGroup(concurrency, group)
        })
      }

      q.on(Queue.Events.EMPTY, () => {
        this.complete = true
        this.emit(Events.COMPLETED)
        // console.log(`ImageService loaded`)
        resolve()
      })
    })
  }

  get = url => {
    return this.images[url]
  }

  get manifestComplied() {
    return this._manifestComplied
  }

  get webp() {
    return this._webp
  }

  set webp(val) {
    return (this._webp = val)
  }
}

let service = new ImageService()
service.Events = Events
window.ImageService = service
export default service
